// packages/console/src/components/panels/LivePipelineLog.tsx
//
// Pipeline log for the LIVE console, ported from the "Pipeline log" block of
// docs/design/ui_kits/aether-live/Transcoder.jsx. Timestamped, tagged, auto-
// scrolling. Lines are REAL LiveLog events off the SSE stream (no fixed pool).
//
// Honest empty state (R10): with no session the stream sends no log lines, so
// the panel reports the transport state (connecting / unavailable / no lines)
// rather than replaying a canned log.

import { useEffect, useRef } from "react";
import { Panel, TChip } from "../Parts";
import type { LiveLog } from "../../api/live";
import type { SseStatus } from "../../api/sse";
import { fmtTimeOfDay } from "../../lib/format";

export function LivePipelineLog({ lines, conn }: { lines: LiveLog[]; conn: SseStatus }) {
  const ref = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines]);

  return (
    <Panel
      title="Pipeline log"
      style={{ height: 148, flex: "none" }}
      bodyStyle={{ padding: 0, flex: 1, minHeight: 0 }}
      right={
        <div style={{ display: "flex", gap: 6 }}>
          <TChip active>All</TChip>
          <TChip>Warnings</TChip>
          <TChip>Errors</TChip>
        </div>
      }
    >
      {lines.length === 0 ? (
        <div style={{ height: "100%", display: "grid", placeItems: "center", color: "var(--fg4)", font: "var(--t-micro)" }}>
          {conn === "open"
            ? "No log lines yet"
            : conn === "error"
              ? "Live stream unavailable"
              : "Connecting to live stream..."}
        </div>
      ) : (
        <div
          ref={ref}
          style={{ height: "100%", overflowY: "auto", padding: "8px 13px", display: "flex", flexDirection: "column", gap: 3 }}
        >
          {lines.map((l, i) => (
            <div
              key={i}
              style={{ display: "flex", gap: 10, font: "400 11px var(--font-mono)", color: "var(--fg2)", whiteSpace: "nowrap" }}
            >
              <span style={{ color: "var(--fg4)" }}>{fmtTimeOfDay(l.at)}</span>
              <span style={{ color: "var(--blue-400)", width: 30 }}>{l.tag}</span>
              <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{l.line}</span>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}
